import { LayoutModuleBold, TripDestinationLight } from '@ha/icons';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Theme, makeStyles, createStyles } from '@material-ui/core';

import Modal from 'ha/ui/next/Modal';
import { Button, units, Grid, Typography } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import Dialog from 'ha/components/next/Dialog';

import SearchFiltersButton from './SearchFilters/SearchFiltersButton';
import { TravelTimeContentForm } from './TravelTime';
import SearchMap from './SearchMap.lazy';
import { getTravelTimePolygon } from '../selectors/mapSelectors';
import useIsTravelTimeEnabled from '../hooks/useIsTravelTimeEnabled';

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    container: {
      position: 'absolute',
      background: t.palette.white.main,
      padding: units.txb(0.625, 0.625, 0.375),
      overflowX: 'auto',
    },
    icon: {
      marginRight: units.rem(0.5),
    },
    description: {
      marginBottom: units.rem(1),
    },
    commuteButton: {
      marginRight: t.spacing(1),
    },
    mobileMapBackdrop: {
      // use the same color as the map while it's loading
      backgroundColor: 'rgb(221, 221, 221) !important',
    },
  }),
);

interface Props {
  isMobileMapOpen: boolean;
  toggleMobileMap: () => void;
}

export const MobileMap: React.FC<Props> = ({
  isMobileMapOpen,
  toggleMobileMap,
}) => {
  const { T } = useIntl();
  const classes = useStyles();
  const { isTravelTimeEnabled } = useIsTravelTimeEnabled();
  const travelTimePolygon = useSelector(getTravelTimePolygon);

  const [isTravelTimeModalOpen, setTravelTimeModalOpen] = useState(false);

  const closeTravelTimeModal = React.useCallback(
    () => setTravelTimeModalOpen(false),
    [],
  );

  return (
    <Modal
      open={isMobileMapOpen}
      disablePortal
      BackdropProps={{ className: classes.mobileMapBackdrop }}
      onClose={toggleMobileMap}
    >
      <React.Fragment>
        <SearchMap />
        <Grid container spacing={1} wrap="nowrap" className={classes.container}>
          <Grid item>
            <Button
              color="primary"
              size="medium"
              variant="outlined"
              onClick={toggleMobileMap}
            >
              <LayoutModuleBold fontSize="small" className={classes.icon} />
              {T('mapControls.gallery')}
            </Button>
          </Grid>
          <Grid item>
            <SearchFiltersButton />
          </Grid>
          {isTravelTimeEnabled && (
            <Grid item>
              <Button
                color="primary"
                size="medium"
                className={classes.commuteButton}
                variant={travelTimePolygon.length ? 'contained' : 'outlined'}
                onClick={() => setTravelTimeModalOpen(true)}
              >
                <TripDestinationLight className={classes.icon} />
                {T('search.map.commute.mobile_button')}
              </Button>
            </Grid>
          )}
        </Grid>
        <Dialog
          show={isTravelTimeModalOpen}
          onClose={closeTravelTimeModal}
          title={T('search.map.commute.mobile_button')}
          dividers
          fullScreen
          content={
            <React.Fragment>
              <Typography.Paragraph className={classes.description}>
                {T('search.map.commute.mobile_description')}
              </Typography.Paragraph>
              <TravelTimeContentForm
                onSubmit={closeTravelTimeModal}
                onReset={closeTravelTimeModal}
                orientation="vertical"
              />
            </React.Fragment>
          }
        />
      </React.Fragment>
    </Modal>
  );
};

export default MobileMap;
